import React, { useEffect, useState } from 'react';
import { Alert, Button, Spinner, StatusBadge } from '@/components/ui';
import ResponsiveModal from '../../../components/ResponsiveModal';
import { fetchPcrSubmissionHistory, type PcrSubmissionHistory } from '../../../services/pcrService';
import { historyWithoutOutboundEmail } from '../utils/pcrHistoryFilters';

interface HistoryEntry extends PcrSubmissionHistory {
  pcrId: number;
}

interface PcrBulkHistoryModalProps {
  visible: boolean;
  selectedIds: number[];
  onClose: () => void;
}

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function stageLabel(stage: string): string {
  const map: Record<string, string> = {
    created: 'Received',
    data_edit: 'Data edit',
    manual_comment: 'Supervisor note',
    promoted_to_work_id: 'Work ID',
    closed_without_work_id: 'Closed (no Work ID)',
    outbound_email: 'Reply sent',
  };
  return map[stage] ?? stage.replace(/_/g, ' ');
}

/** Base badge utilities (mirrors the UI <Badge>) applied to raw stage/pcr badges. */
const BADGE_BASE = 'tw-inline-block tw-px-2 tw-py-1 tw-font-semibold tw-leading-none';

function parseNoteBody(comments: string | null | undefined, stage: string): string | null {
  if (stage === 'outbound_email') return null;
  if (!comments?.trim()) return null;
  if (/^workIdId=\d+$/i.test(comments.trim())) return null;
  try {
    const parsed = JSON.parse(comments) as { _format?: string; body?: string };
    if (parsed._format === 'pcr_note_v1' && typeof parsed.body === 'string') {
      return parsed.body;
    }
    if (parsed._format === 'pcr_outbound_email_v1') return null;
  } catch {
    /* not JSON */
  }
  return comments;
}

const PcrBulkHistoryModal: React.FC<PcrBulkHistoryModalProps> = ({
  visible,
  selectedIds,
  onClose,
}) => {
  const [entries, setEntries] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const idsKey = selectedIds.slice().sort().join(',');

  useEffect(() => {
    if (!visible || selectedIds.length === 0) {
      setEntries([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all(
      selectedIds.map((id) =>
        fetchPcrSubmissionHistory(String(id)).then((rows) =>
          rows.map((r) => ({ ...r, pcrId: id })),
        ),
      ),
    )
      .then((results) => {
        if (cancelled) return;
        const all = historyWithoutOutboundEmail(
          results
            .flat()
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
        );
        setEntries(all);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load history');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, idsKey]);

  const title =
    selectedIds.length === 1
      ? `Notes history – PCR #${selectedIds[0]}`
      : `Notes history – ${selectedIds.length} PCRs`;

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title={title}
      size="large"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {loading ? (
        <div className="tw-text-center tw-py-5">
          <Spinner animation="border" role="status" />
          <p className="tw-text-content-muted tw-text-sm tw-mt-2 tw-mb-0">Loading history…</p>
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : entries.length === 0 ? (
        <p className="tw-text-content-muted tw-text-sm tw-mb-0">No history entries found for the selected PCRs.</p>
      ) : (
        <>
          {selectedIds.length > 1 ? (
            <p className="tw-text-content-muted tw-text-sm tw-mb-2">Excludes outbound email sends (see Contact log per PCR).</p>
          ) : null}
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {entries.map((entry) => {
            const body = parseNoteBody(entry.comments, entry.stage);
            return (
              <div
                key={`${entry.pcrId}-${entry.id}`}
                className="tw-border tw-border-line tw-rounded tw-p-3 tw-mb-2 tw-bg-surface"
                style={{ borderColor: 'rgba(0,0,0,0.1)' }}
              >
                <div className="tw-flex tw-justify-between tw-items-start tw-gap-2 tw-mb-1">
                  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    {selectedIds.length > 1 && (
                      <span
                        className={`${BADGE_BASE} tw-rounded-full`}
                        style={{ background: '#e0e7ff', color: '#3730a3', fontSize: '0.72rem' }}
                      >
                        PCR #{entry.pcrId}
                      </span>
                    )}
                    <StatusBadge status={entry.stage} vocabulary="pcr" label={stageLabel(entry.stage)} className="tw-text-[0.72rem]" />
                  </div>
                  <span className="tw-text-content-muted tw-text-sm tw-whitespace-nowrap tw-shrink-0">
                    {formatWhen(entry.created_at)}
                  </span>
                </div>

                {entry.summary?.trim() ? (
                  <div className="tw-font-semibold tw-text-sm tw-mb-1">{entry.summary}</div>
                ) : null}

                {body && (
                  <p className="tw-mb-1 tw-text-sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {body}
                  </p>
                )}

                <div className="tw-text-sm tw-text-content-muted">
                  {entry.user_name?.trim()
                    ? `By ${entry.user_name}`
                    : entry.external
                      ? 'External (website / webhook)'
                      : 'System'}
                </div>
              </div>
            );
          })}
          </div>
        </>
      )}
    </ResponsiveModal>
  );
};

export default PcrBulkHistoryModal;
